/**
 * FlashFinancialSim — simulación financiera flash:
 *  - Cuota inicial (30% por defecto)
 *  - Mensualidad estimada según precio, plazo y tasa fija
 *
 * Modelo simple para no pretender ser una calculadora bancaria formal.
 * Los valores se muestran con disclaimer.
 */
import React from 'react';
import { Wallet, TrendingDown } from 'lucide-react';

const DEFAULT_TERM_YEARS = 20;
const DEFAULT_ANNUAL_RATE = 0.14;   // 14% nominal anual (referencia Colombia 2025)
const DEFAULT_DOWN_PCT = 0.30;

function monthlyPayment(principal, annualRate, years) {
  if (!principal || principal <= 0) return 0;
  const n = years * 12;
  const r = annualRate / 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

function fmtCOP(n) {
  if (!n || n <= 0) return '$0';
  return `$${Math.round(n).toLocaleString('es-CO')}`;
}

export default function FlashFinancialSim({
  price,
  downPct = DEFAULT_DOWN_PCT,
  years = DEFAULT_TERM_YEARS,
  annualRate = DEFAULT_ANNUAL_RATE,
  className = '',
}) {
  if (!price || price <= 0) return null;

  const downPayment = price * downPct;
  const loan = price - downPayment;
  const monthly = monthlyPayment(loan, annualRate, years);

  return (
    <div
      className={`rounded-xl bg-habita-cream border border-habita/15 p-3 ${className}`}
      data-testid="flash-financial-sim"
    >
      <p className="text-[11px] uppercase tracking-widest font-semibold text-gray-500 mb-2">
        Simulación financiera flash
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-habita shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Cuota inicial ({Math.round(downPct * 100)}%)</p>
            <p className="font-serif text-base font-bold text-habita-dark" data-testid="flash-sim-down">
              {fmtCOP(downPayment)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-habita shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Mensualidad estimada</p>
            <p className="font-serif text-base font-bold text-habita-dark" data-testid="flash-sim-monthly">
              {fmtCOP(monthly)}
            </p>
          </div>
        </div>
      </div>
      <p className="text-[10px] text-gray-500 mt-2 leading-snug">
        Cálculo referencial · plazo {years} años · tasa {(annualRate * 100).toFixed(1)}% E.A.
      </p>
    </div>
  );
}
